import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Footer from '../../components/Footer';
import { blogPosts, blogCategories } from '../../data/blogPosts';
import { formatDate } from '../../data/liveClasses';
import './index.css';

const Blog = () => {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get('category');
  const [category, setCategory] = useState(
    blogCategories.includes(initialCategory) ? initialCategory : 'All'
  );
  const [query, setQuery] = useState('');

  const filteredPosts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogPosts.filter((post) => {
      const matchesCategory = category === 'All' || post.category === category;
      const matchesQuery = !q ||
        post.title.toLowerCase().includes(q) ||
        post.excerpt.toLowerCase().includes(q) ||
        post.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [category, query]);

  const [featured, ...rest] = filteredPosts;

  return (
    <div className="page blog-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-eyebrow">Blog</span>
          <h1 className="page-hero-title">Interview tips &amp; tech insights</h1>
          <p className="page-hero-subtitle">
            Practical guides on cracking interviews, building a career in tech, and the concepts that matter.
          </p>
        </div>
      </section>

      <section className="container">
        <div className="blog-toolbar">
          <div className="blog-chips" role="tablist" aria-label="Filter by category">
            {['All', ...blogCategories].map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={category === c}
                className={`chip ${category === c ? 'active' : ''}`}
                onClick={() => setCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <label className="blog-search">
            <SearchIcon sx={{ fontSize: 20 }} />
            <input
              type="search"
              placeholder="Search articles"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search articles"
            />
          </label>
        </div>

        {filteredPosts.length === 0 && (
          <p className="blog-empty">No articles match your search yet.</p>
        )}

        {featured && (
          <Link to={`/blog/${featured.slug}`} className="blog-featured">
            <span className="blog-category">{featured.category}</span>
            <h2>{featured.title}</h2>
            <p>{featured.excerpt}</p>
            <div className="blog-meta">
              <span>{formatDate(featured.date)}</span>
              <span>·</span>
              <span>{featured.readTime} min read</span>
              <span className="blog-read">Read article <ArrowForwardIcon sx={{ fontSize: 18 }} /></span>
            </div>
          </Link>
        )}

        <div className="blog-grid">
          {rest.map((post) => (
            <Link to={`/blog/${post.slug}`} className="blog-card" key={post.slug}>
              <span className="blog-category">{post.category}</span>
              <h3>{post.title}</h3>
              <p>{post.excerpt}</p>
              <div className="blog-meta">
                <span>{formatDate(post.date)}</span>
                <span>·</span>
                <span>{post.readTime} min read</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
